import { createReducer, on } from '@ngrx/store';
import { Constants, StwProcessCode, transactionTypes } from '../../shared/utilities/Constants';
import { ViewPageActions } from '../../transaction-view/actions';
import { ConfirmationPageActions, InternalTransferActions, InternalTransfersActions, ReviewPageActions, StandingAuthorizationsActions, TransactionFormActions } from '../actions';
import { InternalTransferInfo } from '../models/internal-transfer/InternalTransferInfo';
import { TaxAmountAndPercentageFormatter } from '../../shared/utilities/TaxAmountAndPercentageFormatter';
import { FormatDatePipe } from '../../shared/pipes/FormatDatePipe';
import { ItTransactionDetails } from '../models/form/TransactionForm';
import { DateTime } from 'luxon';
import { AccountInfoActions } from '../../account-info-header/actions';
import { EAuthModelCustomer } from '../models/internal-transfer/EAuthModelCustomer';
import { AccountInfoResponse } from '../../account-info-header/models';
import { InitReleaseActions } from '../../initiate-and-release/actions';
import { CodeAndDescription } from '../models/shared/CodeAndDescription';
import { DistributionReason } from '../models/internal-transfer/DistributionReason';
import { PositionLots } from '../models/internal-transfer/PositionLots';
import { TableColumn } from '../../shared/types/TableColumn';
import { DecimalPipe } from '@angular/common';
import { getDateWOTimezone } from '../../shared/utilities/datetime-utils';

export const internalTransferInfoKey = 'internalTransferInfo';

export interface State extends InternalTransferInfo { }

export const initialInternalTransferInfoState: State = {
  processDate: '',
  stateCd: '',
  isRMDEligible: false,
  rmdAnnualRemainingAmount: '',
  hasPositionsAccess: false,
  hasPositions: false,
  positionsValidIraContributionCode: '',
  acknowledgementArtifactContent: '',
  isLoaOnFile: false,
  iraContributionTypeMap: [],
  toAccountInfo: {
    accountNumber: '',
    registrationType: '',
    regTypeShortName: '',
    productCode: '',
    isIdentical: false,
    isRelated: false,
    isIraAccount: false,
    isTrust: false,
    toIncome: '100%',
    toPrincipal: '0%',
    accountRegistration: [],
    availableCashMmf: ''
  },
  iraFields: {
    isIraTransaction: false,
    isAccountTaxable: false,
    hasActiveTaxElections: false,
    fedTaxPercent: '',
    fedTaxAmount: '',
    isWithholdFedTax: false,
    stateTaxPercent: '',
    stateTaxAmount: '',
    stateTaxAttentionRequired: false,
    isWithholdStateTax: false,
    stateTypeIndicator: '',
    stwProcessCode: '',
    stateTaxAttentionMessage: '',
    isCashieringIRAAgreementOpened: false,
    rmdAnnualAmount: '',
    rmdAnnualPaidYearToDateAmount: '',
    isRmdToDisplay: false,
    taxValueOriginMessage: '',
    distributionReason: {
      code: '',
      subCode: '',
      description: '',
      isTaxExempt: false
    },
    distributionReasonMap: [],
    nraAccountIndicator: false
  },
  recurringFields: {
    isRecurringTransaction: false,
    frequencyList: [],
    payIncomeMap: [],
    frequency: {
      code: '',
      description: ''
    },
    payIncome: {
      code: '',
      description: ''
    },
    endDate: '',
    nextProcessDate: '',
    secondProcessDate: ''
  },
  trustFields: {
    isTrust: false,
    trustTaxCode: '',
    trustDepositCode: '',
    trustIncmPct: '',
    trustIncmToPct: '',
    trustPrinPct: '',
    trustPrinToPct: '',
    tranMemo: '',
    selTaxCatIndex: '',
    selTaxCodeIndex: ''
  },
  accountInfoBanner: {
    masterAccount: '',
    subAccountNumber: '',
    regShortName: '',
    subAccountNameLine1: '',
    subAccountNameLine2: '',
    subAccountNameLine3: '',
    availableCashMMFAmount: '',
    availableOnMarginAmount: '',
    rmdAnnualAmount: '',
    rmdAnnualPaidYearToDateAmount: ''
  },
  warningsList: [],
  errorText: '',
  taxCodeList: {},
  isTrustNonPTRSAccount: false,
  pjlsEnabled: false,
  isIdentical: false,
  hasWpfaRole: false,
  hasMoveMoneyDataAccess: false,
  eauthAccess: false,
  clientAuthorizationNeeded: false,
  isExpandReportingJournal: false,
  isRMDToDisplay: false,
  isThirdParty: false,
  eAuthModel: {
    isTransactionFromSLOA: false,
    eauthSelected: false,
    isTransactionFromSLOAOrSLOA: false,
    numberOfAuthRequired: 0,
    eauthEligibilityServiceSLOAError: false,
    eauthEligibilityServiceDown: false,
    eauthEligibilityServiceSLOAErrorMsg: '',
    isOrgAccount: false,
    accountRestricted: false,
    orgOrEduSavingsAccount: false,
    custAcctAuthorizationsListEmpty: false,
    eauthInd: false,
    eauthSLOAInd: false,
    allAccountHolderIneligible: false,
    numOfIneligibleCust: 0,
    eauthEligibilityServiceDownErrorMsg: '',
    custAcctAuthorizationsList: {
      allAccountHoldersIneligible: false,
      numOfIneligibleCust: 0,
      numOfNotWebEnabledCust: 0,
      totalNumOfAccountHolders: 0,
      eauthInd: false,
      eAuthCustomerList: []
    },
    custAcctSLOAAuthorizationsList: {
      allAccountHoldersIneligible: false,
      numOfIneligibleCust: 0,
      numOfNotWebEnabledCust: 0,
      totalNumOfAccountHolders: 0,
      eauthInd: false,
      eAuthCustomerList: []
    },
    transactionType: '',
    submitButtonsDisable: '',
    txnType: '',
  },
  canViewJournalWithPositions: false,
  accountRegistration: [],
  journalCashAmount: '',
  totalCashAmount: '',
  totalWithdrawalAmount: '',
  iraContributionType: {
    code: '',
    subCode: '',
    year: '',
    description: ''
  },
  pendReasons: [],
  attentionLabelStatus: false,
  isPendingOnlyForCTWE: false,
  isPendingForLPND: false,
  messageInfo: {
    errorList: [],
    warningList: [],
    atsSystemErrors: []
  },
  loginId: '',
  source: {
    code: '',
    description: ''
  },
  trustTaxCode: '',
  nickname: '',
  hasError: false,
  serviceError: '',
  positionsInfo: {
    hasSelected: false,
    cmtRequestId: 0,
    count: 0,
    list: [],
    totalMarketValue: '',
    selectedMarketValue: '',
    hasError: false,
    serviceError: '',
    lotSelectionMethodDesc: '',
    id: 0,
    stringId: '',
    costBasisMethodSelected: '',
    defaultCostBasisMethod: ''
  },
  taxId: '',
  caseId: '',
  submitAndCreateSloaId: '',
  status: {
    code: '',
    description: ''
  },
  requestedDate: '',
  requestedTime: '',
  cashieringReqId: 0,
  cashieringReqSecondaryId: 0,
  isPendingForCTWE: false,
  eAuthTransactionScoreCardLogMessage: '',
  eAuthSloaScoreCardLogMessage: '',
  inputCallFinished: false,
  inputCallSuccessful: false,
  positionLotCallFinished: false,
  verifyCallFinished: false,
  acknowledgeCallFinished: false,
  toAccountState: {
    loading: false,
    error: '',
    hasError: false
  },
  inputIraFields: {
    isIraTransaction: false,
    isAccountTaxable: false,
    hasActiveTaxElections: false,
    fedTaxPercent: '',
    fedTaxAmount: '',
    isWithholdFedTax: false,
    stateTaxPercent: '',
    stateTaxAmount: '',
    stateTaxAttentionRequired: false,
    isWithholdStateTax: false,
    stateTypeIndicator: '',
    stwProcessCode: '',
    stateTaxAttentionMessage: '',
    isCashieringIRAAgreementOpened: false,
    rmdAnnualAmount: '',
    rmdAnnualPaidYearToDateAmount: '',
    isRmdToDisplay: false,
    taxValueOriginMessage: '',
    distributionReason: {
      code: '',
      subCode: '',
      description: '',
      isTaxExempt: false
    },
    distributionReasonMap: [],
    nraAccountIndicator: false
  },
  positionsLotDetails: {
    costMethod: '',
    totals: {
      totalEstimatedMarketValue: 0,
      totalQuantity: 0,
      totalNoOfPositions: 0
    },
    positions: [],
    hasError: false,
    serviceError: '',
    id: ''
  },
  rowData: [],
  loadToAccountErrorText: '',
  verifyErrorText: '',
  standingAuthCallSucceeded: false,
  shouldShowStateTax: false,
  netCashAmount: "",
  isSubmitAndCreate: false,
  txnCode: '',
  cashieringIraAgreementText: '',
  journalAuthResponse: {
    draftId: '',
    pendForRelease: false,
    pendForReleaseReason: '',
    canRelease: false
  },
  revalidationStatus: '',
  revalidation: {
    status: '',
    errors: [],
    fieldsUpdated: []
  }
}

export const internalTransferReducer = createReducer(
  initialInternalTransferInfoState,
  on(InternalTransferActions.loadToAccountSuccess, (state, response) => {
    const { internalTransferInputResponse } = response;
    const toAccountHasPtrsOffer = internalTransferInputResponse.toAccountInfo.isTrust && !internalTransferInputResponse.isTrustNonPTRSAccount;
    const defaultToIncome = toAccountHasPtrsOffer ? '0%' : '100%';
    const defaultToPrincipal = toAccountHasPtrsOffer ? '100%' : '0%';
    return {
      ...state,
      inputCallSuccessful: true,
      processDate: internalTransferInputResponse.processDate,
      stateCd: internalTransferInputResponse.stateCd,
      isRMDEligible: internalTransferInputResponse.isRMDEligible,
      rmdAnnualRemainingAmount: internalTransferInputResponse.rmdAnnualRemainingAmount,
      hasPositionsAccess: internalTransferInputResponse.hasPositionsAccess,
      hasPositions: internalTransferInputResponse.hasPositions,
      positionsValidIraContributionCode: internalTransferInputResponse.positionsValidIraContributionCode,
      iraContributionTypeMap: internalTransferInputResponse.iraContributionTypeMap,
      toAccountInfo: {
        accountNumber: internalTransferInputResponse.toAccountInfo.accountNumber,
        registrationType: internalTransferInputResponse.toAccountInfo.registrationType,
        regTypeShortName: internalTransferInputResponse.toAccountInfo.regTypeShortName,
        productCode: internalTransferInputResponse.toAccountInfo.productCode,
        isIdentical: internalTransferInputResponse.isIdentical,
        isRelated: internalTransferInputResponse.toAccountInfo.isRelated,
        isIraAccount: internalTransferInputResponse.toAccountInfo.isIraAccount,
        isTrust: internalTransferInputResponse.toAccountInfo.isTrust,
        toIncome: internalTransferInputResponse.toAccountInfo.toIncome.length !== 0 ? internalTransferInputResponse.toAccountInfo.toIncome : defaultToIncome,
        toPrincipal: internalTransferInputResponse.toAccountInfo.toPrincipal.length !== 0 ? internalTransferInputResponse.toAccountInfo.toPrincipal : defaultToPrincipal,
        accountRegistration: internalTransferInputResponse.toAccountInfo.accountRegistration,
        availableCashMmf: internalTransferInputResponse.toAccountInfo.availableCashMmf
      },
      iraFields: {
        isIraTransaction: internalTransferInputResponse.iraFields.isIraTransaction,
        isAccountTaxable: internalTransferInputResponse.iraFields.isAccountTaxable,
        hasActiveTaxElections: internalTransferInputResponse.iraFields.hasActiveTaxElections,
        fedTaxPercent: internalTransferInputResponse.iraFields.fedTaxAmount, //at the input step, "amount" is actually the percent (only true for journals)
        fedTaxAmount: '',
        isWithholdFedTax: internalTransferInputResponse.iraFields.isWithholdFedTax,
        stateTaxPercent: internalTransferInputResponse.iraFields.stateTaxAmount,
        stateTaxAmount: '',
        stateTaxAttentionRequired: internalTransferInputResponse.iraFields.stateTaxAttentionRequired,
        isWithholdStateTax: internalTransferInputResponse.iraFields.isWithholdStateTax,
        stateTypeIndicator: internalTransferInputResponse.iraFields.stateTypeIndicator,
        stwProcessCode: internalTransferInputResponse.iraFields.stwProcessCode,
        stateTaxAttentionMessage: internalTransferInputResponse.iraFields.stateTaxAttentionMessage,
        isCashieringIRAAgreementOpened: internalTransferInputResponse.iraFields.isCashieringIRAAgreementOpened,
        rmdAnnualAmount: internalTransferInputResponse.iraFields.rmdAnnualAmount,
        rmdAnnualPaidYearToDateAmount: internalTransferInputResponse.iraFields.rmdAnnualPaidYearToDateAmount,
        isRmdToDisplay: internalTransferInputResponse.iraFields.isRmdToDisplay,
        taxValueOriginMessage: internalTransferInputResponse.iraFields.taxValueOriginMessage,
        distributionReason: internalTransferInputResponse.iraFields.distributionReason,
        distributionReasonMap: internalTransferInputResponse.iraFields.distributionReasonMap,
        nraAccountIndicator: internalTransferInputResponse.iraFields.nraAccountIndicator,
      },
      recurringFields: internalTransferInputResponse.recurringFields,
      trustFields: internalTransferInputResponse.trustFields,
      accountInfoBanner: internalTransferInputResponse.accountInfoBanner,
      warningsList: internalTransferInputResponse.warningsList,
      errorText: internalTransferInputResponse.errorText,
      taxCodeList: internalTransferInputResponse.taxCodeList,
      isTrustNonPTRSAccount: internalTransferInputResponse.isTrustNonPTRSAccount,
      pjlsEnabled: internalTransferInputResponse.pjlsEnabled,
      isIdentical: internalTransferInputResponse.isIdentical,
      hasWpfaRole: internalTransferInputResponse.hasWpfaRole,
      hasMoveMoneyDataAccess: internalTransferInputResponse.hasMoveMoneyDataAccess,
      inputCallFinished: true,
      positionLotCallFinished: false,
      verifyCallFinished: false,
      serviceError: '',
      toAccountState: {
        loading: false,
        error: '',
        hasError: false
      },
      inputIraFields: {
        isIraTransaction: internalTransferInputResponse.iraFields.isIraTransaction,
        isAccountTaxable: internalTransferInputResponse.iraFields.isAccountTaxable,
        hasActiveTaxElections: internalTransferInputResponse.iraFields.hasActiveTaxElections,
        fedTaxPercent: internalTransferInputResponse.iraFields.fedTaxAmount, //at the input step, "amount" is actually the percent (only true for journals)
        fedTaxAmount: '',
        isWithholdFedTax: internalTransferInputResponse.iraFields.isWithholdFedTax,
        stateTaxPercent: internalTransferInputResponse.iraFields.stateTaxAmount,
        stateTaxAmount: '',
        stateTaxAttentionRequired: internalTransferInputResponse.iraFields.stateTaxAttentionRequired,
        isWithholdStateTax: internalTransferInputResponse.iraFields.isWithholdStateTax,
        stateTypeIndicator: internalTransferInputResponse.iraFields.stateTypeIndicator,
        stwProcessCode: internalTransferInputResponse.iraFields.stwProcessCode,
        stateTaxAttentionMessage: internalTransferInputResponse.iraFields.stateTaxAttentionMessage,
        isCashieringIRAAgreementOpened: internalTransferInputResponse.iraFields.isCashieringIRAAgreementOpened,
        rmdAnnualAmount: internalTransferInputResponse.iraFields.rmdAnnualAmount,
        rmdAnnualPaidYearToDateAmount: internalTransferInputResponse.iraFields.rmdAnnualPaidYearToDateAmount,
        isRmdToDisplay: internalTransferInputResponse.iraFields.isRmdToDisplay,
        taxValueOriginMessage: internalTransferInputResponse.iraFields.taxValueOriginMessage,
        distributionReason: internalTransferInputResponse.iraFields.distributionReason,
        distributionReasonMap: internalTransferInputResponse.iraFields.distributionReasonMap,
        nraAccountIndicator: internalTransferInputResponse.iraFields.nraAccountIndicator
      },
      loadToAccountErrorText: '',
      shouldShowStateTax: hasStateTax(
        internalTransferInputResponse.iraFields.nraAccountIndicator,
        internalTransferInputResponse.iraFields.stwProcessCode as StwProcessCode
      ),
      cashieringIraAgreementText: internalTransferInputResponse.cashieringIraAgreementText
    }
  }),
  on(InternalTransferActions.loadToAccountFail, (state, err) => {
    return {
      ...state,
      inputCallSuccessful: false,
      serviceError: err.message,
      toAccountState: {
        loading: false,
        error: '',
        hasError: true
      },
    }
  }),
  on(InternalTransferActions.loadToAccountFinishedWithError, (state, response) => {
    const { internalTransferInputResponse } = response;
    const toAccountHasPtrsOffer = internalTransferInputResponse?.toAccountInfo?.isTrust && !internalTransferInputResponse?.isTrustNonPTRSAccount;
    const defaultToIncome = toAccountHasPtrsOffer ? '0%' : '100%';
    const defaultToPrincipal = toAccountHasPtrsOffer ? '100%' : '0%';
    return {
      ...state,
      inputCallSuccessful: false,
      loadToAccountErrorText: internalTransferInputResponse.errorText,
      toAccountInfo: {
        accountNumber: internalTransferInputResponse?.toAccountInfo?.accountNumber,
        registrationType: internalTransferInputResponse?.toAccountInfo?.registrationType,
        regTypeShortName: internalTransferInputResponse?.toAccountInfo?.regTypeShortName,
        productCode: internalTransferInputResponse?.toAccountInfo?.productCode,
        isIdentical: internalTransferInputResponse?.isIdentical,
        isRelated: internalTransferInputResponse?.toAccountInfo?.isRelated,
        isIraAccount: internalTransferInputResponse?.toAccountInfo?.isIraAccount,
        isTrust: internalTransferInputResponse?.toAccountInfo?.isTrust,
        toIncome: internalTransferInputResponse?.toAccountInfo?.toIncome.length !== 0 ? internalTransferInputResponse?.toAccountInfo?.toIncome : defaultToIncome,
        toPrincipal: internalTransferInputResponse?.toAccountInfo?.toPrincipal.length !== 0 ? internalTransferInputResponse?.toAccountInfo?.toPrincipal : defaultToPrincipal,
        accountRegistration: internalTransferInputResponse?.toAccountInfo?.accountRegistration,
        availableCashMmf: internalTransferInputResponse?.toAccountInfo?.availableCashMmf
      },
      toAccountState: {
        loading: false,
        error: '',
        hasError: false
      },
    }
  }),
  on(InternalTransferActions.verifyCallFinishedWithError, (state, { type, ...action }) => {
    const { response } = action;
    return {
      ...state,
      verifyErrorText: response.messageInfo.atsSystemErrors.length > 0 ? Constants.createRequestServiceError : response.messageInfo.errorList[0]?.messageText,
      verifyCallFinished: false,
      toAccountState: {
        loading: false,
        error: '',
        hasError: false
      },
      journalAuthResponse: response?.journalAuthResponse,
      acknowledgementArtifactContent: response?.acknowledgementArtifactContent,
      revalidation: {
        ...response.revalidation,
        status: response.revalidation?.status ?? '',
        errors: response.revalidation?.errors ?? [],
        fieldsUpdated: response.revalidation?.fieldsUpdated ?? [] 
      }
    }
  }),
  on(InternalTransferActions.toAccountEntered, (state) => {
    return {
      ...state,
      inputCallFinished: false,
      positionLotCallFinished: false,
      verifyCallFinished: false,
      loadToAccountErrorText: '',
      toAccountState: {
        loading: true,
        error: '',
        hasError: false
      },
    }
  }),
  on(InternalTransferActions.transactionDetailsComponentLoaded, (state) => {
    return {
      ...state
    }
  }),
  on(InternalTransferActions.loadPositionsSuccessful,
    InternalTransfersActions.itLoadPositionsSuccessful,
    (state, { type, ...action }) => {
      const { internalTransferPositionsResponse } = action;
      return {
        ...state,
        positionsLotDetails: {
          ...state.positionsLotDetails,
          costMethod: internalTransferPositionsResponse.costMethod,
          totals: {
            ...state.positionsLotDetails.totals,
            totalEstimatedMarketValue: internalTransferPositionsResponse.totals.totalEstimatedMarketValue,
            totalQuantity: internalTransferPositionsResponse.totals.totalQuantity,
            totalNoOfPositions: internalTransferPositionsResponse.totals.totalNoOfPositions
          },
          positions: internalTransferPositionsResponse.positions,
          hasError: internalTransferPositionsResponse.hasError,
          serviceError: internalTransferPositionsResponse.serviceError
        },
        positionLotCallFinished: true
      }
    }),
  on(InternalTransferActions.updateRowData, (state, { type, ...action }) => {
    const { response } = action;
    return {
      ...state,
      rowData: response
    }

  }),
  on(InternalTransferActions.loadPositionsFail, (state, { type, ...action }) => {
    return {
      ...state,
      serviceError: action.message
    }
  }),
  on(TransactionFormActions.callInternalTransferVerify, (state) => {
    return {
      ...state,
      verifyCallFinished: false,
    }
  }),
  on(InternalTransferActions.verifyCallSuccessful, (state, { type, ...action }) => {
    const { response } = action;
    const stateTax = TaxAmountAndPercentageFormatter(
      response.iraFields.stateTaxAmount
    );
    const fedTax = TaxAmountAndPercentageFormatter(
      response.iraFields.fedTaxAmount
    )
    return {
      ...state,
      eauthAccess: response.eauthAccess,
      accountInfoBanner: response.accountInfoBanner,
      clientAuthorizationNeeded: response.clientAuthorizationNeeded,
      isExpandReportingJournal: response.isExpandReportingJournal,
      isRMDToDisplay: response.isRMDToDisplay,
      isThirdParty: response.isThirdParty,
      eAuthModel: response.eAuthModel,
      canViewJournalWithPositions: response.canViewJournalWithPositions,
      acknowledgementArtifactContent: response.acknowledgementArtifactContent,
      iraFields: {
        ...state.iraFields,
        isIraTransaction: response.iraFields.isIraTransaction,
        isAccountTaxable: response.iraFields.isAccountTaxable,
        hasActiveTaxElections: response.iraFields.hasActiveTaxElections,
        fedTaxAmount: ((fedTax.amount === '' && fedTax.percentage === '') && response.positionsInfo.hasSelected) ? '' : response.iraFields.fedTaxAmount,
        fedTaxPercent: (fedTax.percentage === '' && response.positionsInfo.hasSelected) ? response.iraFields.fedTaxAmount : fedTax.percentage,
        isWithholdFedTax: response.iraFields.isWithholdFedTax,
        stateTaxAmount: ((stateTax.amount === '' && stateTax.percentage === '') && response.positionsInfo.hasSelected) ? '' : response.iraFields.stateTaxAmount,
        stateTaxPercent: (stateTax.percentage === '' && response.positionsInfo.hasSelected) ? response.iraFields.stateTaxAmount : stateTax.percentage,
        stateTaxAttentionRequired: response.iraFields.stateTaxAttentionRequired,
        isWithholdStateTax: response.iraFields.isWithholdStateTax,
        stateTypeIndicator: response.iraFields.stateTypeIndicator,
        stwProcessCode: response.iraFields.stwProcessCode,
        stateTaxAttentionMessage: response.iraFields.stateTaxAttentionMessage,
        isCashieringIRAAgreementOpened: response.iraFields.isCashieringIRAAgreementOpened,
        rmdAnnualAmount: response.iraFields.rmdAnnualAmount,
        rmdAnnualPaidYearToDateAmount: response.iraFields.rmdAnnualPaidYearToDateAmount,
        isRmdToDisplay: response.iraFields.isRmdToDisplay,
        taxValueOriginMessage: response.iraFields.taxValueOriginMessage,
        distributionReason: response.iraFields.distributionReason,
        distributionReasonMap: response.iraFields.distributionReasonMap,
        nraAccountIndicator: response.iraFields.nraAccountIndicator
      },
      toAccountInfo: response.toAccountInfo,
      accountRegistration: response.accountRegistration,
      processDate: response.processDate,
      journalCashAmount: response.journalCashAmount,
      totalCashAmount: response.totalCashAmount,
      totalWithdrawalAmount: response.totalWithdrawalAmount,
      iraContributionType: response.iraContributionType,
      pendReasons: response.pendReasons,
      attentionLabelStatus: response.attentionLabelStatus,
      isPendingOnlyForCTWE: response.isPendingOnlyForCTWE,
      isPendingForLPND: response.isPendingForLPND,
      messageInfo: response.messageInfo,
      trustFields: response.trustFields,
      loginId: response.loginId,
      source: response.source,
      trustTaxCode: response.trustTaxCode,
      recurringFields: {
        ...state.recurringFields,
        frequency: response.recurringFields.frequency,
        payIncome: response.recurringFields.payIncome,
        isRecurringTransaction: response.recurringFields.isRecurringTransaction,
        endDate: response.recurringFields.endDate,
        secondProcessDate: response.recurringFields.secondProcessDate,
        nextProcessDate: response.recurringFields.nextProcessDate,
      },
      positionsInfo: response.positionsInfo,
      nickname: response.nickname,
      hasError: response.hasError,
      verifyCallFinished: true,
      serviceError: '',
      verifyErrorText: '',
      isSubmitAndCreate: false,
      journalAuthResponse: response.journalAuthResponse,
      revalidationStatus: response.revalidation?.status ?? '',
      revalidation: {
        ...response.revalidation,
        status: response?.revalidation?.status ?? '',
        errors: response?.revalidation?.errors ?? [],
        fieldsUpdated: response?.revalidation?.fieldsUpdated ?? []
      },
      shouldShowStateTax: hasStateTax(
        response.iraFields.nraAccountIndicator,
        response.iraFields.stwProcessCode as StwProcessCode
      ),
    }
  }),
  on(InitReleaseActions.initReleaseByDraftIdSuccessful, (state, { response, isSloa }) => {
    if (isSloa || response.transactionType !== 'journal') return state;
    let totalQuantityVal : number = 0;
    const decimalPipe = new DecimalPipe('en-US');
    let totalEMV : number=0;
    let positionData = response.draftObject.selectedPositionsList?.map(position => {
      totalQuantityVal = totalQuantityVal + position.quantity;
      let hasLots = position.lots.length > 0;
      totalEMV = totalEMV + position.selectedMarketValue;
      let totalCostBasis = position.lots.reduce((acc: any, curr: any) => acc + (parseFloat(curr.costBasis) / parseFloat(curr.quantity)) * parseFloat(curr.selectedQuantity) * Math.sign(parseFloat(curr.costBasis)), 0);
      totalCostBasis = Number(totalCostBasis.toFixed(2));
      return {
        "Symbol": position.symbol,
        "Name": position.fullName,
        "RowHeight": '',
        "Available shares": `${decimalPipe.transform(position.quantity, '1.0-5')} (${position.numberOfLots} ${position.numberOfLots === 1 ? 'lot' : 'lots'})`,
        "Current price/share": `$${decimalPipe.transform(position.price, '1.0-5')}`,
        "Shares to move": Math.abs(Number(position.selectedQuantity)),
        "Estimated market value": `$${position.selectedMarketValue}`,
        "Cost basis": position.costBasis,
        lotDetail: position.lots.map((lot: { dateAcquired: string; selectedQuantity: string; holdingPeriodCode: string; purchasePrice: number; lotId: string; lotsToMove: string; costBasis: string; marketValue: number; quantity: number; }) => {
          return {
            acquiredDate: getDateWOTimezone(lot.dateAcquired),
            lotQuantity: lot.quantity,
            holdingPeriod: lot.holdingPeriodCode === 'S' ? 'Short' : lot.holdingPeriodCode === 'L' ? 'Long' : '',
            purchaseDate: lot.dateAcquired,
            purchasePrice: lot.purchasePrice,
            lotId: lot.lotId,
            lotsToMove: lot.selectedQuantity,
            costBasis: lot.costBasis,
            estimatedMarketValue: `$${lot.marketValue}`,
            totalCostBasis: `$${(((parseFloat(lot.costBasis) / lot.quantity) * parseFloat(lot.selectedQuantity) * Math.sign(parseFloat(lot.costBasis)))).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          }
        }),
        cmtSequenceId: position.cmtSequenceId,
        schwabSecurityId: position.schwabSecurityId,
        "valMultiplier": position.multiplier,
        "Total cost basis": hasLots ? `$${totalCostBasis.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A',
        "nodeCostBasis": response.draftObject.selectedCostBasisMethod ?? '',
        'setToAverage': '',
        "restrictions": {
          isRestricted: position.isRestricted,
          restrictionText: position.restrictionText
        },
        numberOfLots: position.numberOfLots
      }
    });
    return {
      ...state,
      stateCd: response.draftObject.stateCd ?? '',
      rowData: positionData as TableColumn[],
      iraFields: {
        ...state.iraFields,
        isIraTransaction: !!response.draftObject.isIraTransaction,
        fedTaxPercent: response.draftObject.fedTaxAmount ? `${response.draftObject.fedTaxAmount.toString()}%` : '',
        stateTaxPercent: response.draftObject.stateTaxAmount ? `${response.draftObject.stateTaxAmount.toString()}%` : '',
        distributionReasonMap: response.draftObject.distributionReason as DistributionReason ? [response.draftObject.distributionReason as DistributionReason ?? new DistributionReason()] : []
      },
      inputIraFields: {
        ...state.inputIraFields,
        isIraTransaction: !!response.draftObject.isIraTransaction,
        distributionReasonMap: response.draftObject.distributionReason as DistributionReason ? [response.draftObject.distributionReason as DistributionReason ?? new DistributionReason()] : []
      },
      positionsInfo: {
        ...state.positionsInfo,
        cmtRequestId: response.draftObject.stockHandlerId ?? 0
      },
      recurringFields: {
        ...state.recurringFields,
        payIncomeMap: response.draftObject.payIncome ? [response.draftObject.payIncome ?? new CodeAndDescription()] : [new CodeAndDescription()],
        frequencyList: (response.draftObject.frequency as CodeAndDescription) ?
          [(response.draftObject.frequency as CodeAndDescription) ?? new CodeAndDescription] : [new CodeAndDescription()],
        isRecurringTransaction: response.draftObject.isRecurringTransaction
      },
      iraContributionTypeMap: response.draftObject.iraContributionType ? [response.draftObject.iraContributionType] : [],
      cashieringReqId: Number(response.draftObject.cashieringRequestId),
      cashieringReqSecondaryId: response.draftObject.cashieringSecondRequestId ?? 0,
      positionsLotDetails: {
        ...state.positionsLotDetails,
        costMethod: response.draftObject.defaultCostBasisMethod ?? '',
        positions: response.draftObject.selectedPositionsList as PositionLots[],
        totals: {
          ...state.positionsLotDetails.totals,
          totalEstimatedMarketValue: totalEMV,
          totalQuantity: totalQuantityVal,
          totalNoOfPositions: response.draftObject.selectedPositionsCount ?? 0
        },
      },
      positionLotCallFinished: true
    }
  }),
  on(InternalTransferActions.verifyCallFail, (state, { type, ...action }) => {
    return {
      ...state,
      serviceError: action.message,
      hasError: true
    }
  }),
  on(TransactionFormActions.loadInternalTransferWithoutVerifyCall, (state, form) => {
    const details = form.transactionDetails as ItTransactionDetails;
    return {
      ...state,
      verifyCallFinished: true,
      totalCashAmount: details.amount ? details.amount : Constants.zeroAmount,
      journalCashAmount: details.amount ? details.amount : Constants.zeroAmount,
      processDate: details.processDate,
      requestedDate: DateTime.now().toFormat('MM/dd/yyyy'),
      iraContributionType: { code: '', subCode: '', year: '', description: details.iraContributionType },
      clientAuthorizationNeeded: true,
      iraFields: {
        ...state.iraFields,
        fedTaxPercent: details.taxWithholdingsFormGroup?.fedTaxPercent,
        stateTaxPercent: details.taxWithholdingsFormGroup?.stateTaxPercent
      }
    }
  }),
  on(InternalTransferActions.acknowledgeCallSuccessful, (state, { type, ...action }) => {
    const { response } = action;
    return {
      ...state,
      loginId: response.loginId,
      taxId: response.taxId,
      caseId: response.caseId,
      submitAndCreateSloaId: response.submitAndCreateSloaCaseId,
      source: response.source,
      status: response.status,
      pendReasons: response.pendReasons,
      requestedDate: response.requestedDate,
      requestedTime: response.requestedTime,
      messageInfo: response.messageInfo,
      cashieringReqId: response.cashieringReqId,
      cashieringReqSecondaryId: response.cashieringReqSecondaryId,
      isPendingForCTWE: response.isPendingForCTWE,
      eAuthTransactionScoreCardLogMessage: response.eAuthSloaScoreCardLogMessage,
      eAuthSloaScoreCardLogMessage: response.eAuthSloaScoreCardLogMessage,
      hasError: response.hasError,
      inputCallFinished: false,
      verifyCallFinished: false,
      serviceError: '',
      acknowledgeCallFinished: true
    }
  }),
  on(InternalTransferActions.acknowledgeCallFail, (state, { type, ...action }) => {
    return {
      ...state,
      serviceError: action.message
    }
  }),
  on(TransactionFormActions.loadReviewPage, state => {
    return {
      ...state,
      verifyCallFinished: false
    }
  }),
  on(InternalTransferActions.nsfClientSideError, (state) => {
    return {
      ...state,
      serviceError: 'INSUFFICIENT FUNDS FOR THIS REQUEST'
    }
  }),
  on(TransactionFormActions.distributionReasonChanged, (state, { type, ...action }) => {
    if (action.transactionType === transactionTypes.internalTransfer) {
      return {
        ...state,
        inputIraFields: {
          ...state.inputIraFields,
          distributionReason: action.distributionReason
        }
      }
    }
    return { ...state }
  }),
  on(ConfirmationPageActions.createAnotherRequestClicked,
    ReviewPageActions.cancelClicked,
    ConfirmationPageActions.viewTransactionStatusClicked,
    TransactionFormActions.cancelClicked,
    InternalTransferActions.clearToAccount,
    InternalTransferActions.extraPromptUpdated,
    InitReleaseActions.getInitReleaseList, () => {
      return initialInternalTransferInfoState;
    }),
  on(InternalTransferActions.callInquiry, (state, { type, ...action }) => {
    return {
      ...state,
      cashieringReqId: parseInt(action.payload.cashieringRqstId),
      txnCode: action.payload.txnCode ?? ''
    }
  }),
  on(InternalTransferActions.inquiryCallSuccessful, (state, response) => {
    const { internalTransferInquiryResponse } = response;
    const formatDatePipe = new FormatDatePipe();
    const stateTax = TaxAmountAndPercentageFormatter(
      internalTransferInquiryResponse.iraFields.stateTaxAmount
    );
    const fedTax = TaxAmountAndPercentageFormatter(
      internalTransferInquiryResponse.iraFields.fedTaxAmount
    )
    return {
      ...state,
      nickname: internalTransferInquiryResponse.nickname,
      totalCashAmount: internalTransferInquiryResponse.totalCashAmount,
      totalWithdrawalAmount: internalTransferInquiryResponse.totalWithdrawalAmount,
      journalCashAmount: internalTransferInquiryResponse.netCashAmount,
      netCashAmount: internalTransferInquiryResponse.netCashAmount,
      toAccountInfo: internalTransferInquiryResponse.toAccountInfo,
      recurringFields: internalTransferInquiryResponse.recurringFields,
            isLoaOnFile: internalTransferInquiryResponse.isLoaOnFile,
      iraFields: {
        ...internalTransferInquiryResponse.iraFields,
        fedTaxPercent: fedTax.percentage !== '' ? fedTax.percentage : internalTransferInquiryResponse.iraFields.fedTaxAmount,
        stateTaxPercent: stateTax.percentage !== '' ? stateTax.percentage : internalTransferInquiryResponse.iraFields.stateTaxAmount
      },
      inputIraFields: {
        ...internalTransferInquiryResponse.iraFields,
        fedTaxPercent: fedTax.percentage !== '' ? fedTax.percentage : internalTransferInquiryResponse.iraFields.fedTaxAmount,
        stateTaxPercent: stateTax.percentage !== '' ? stateTax.percentage : internalTransferInquiryResponse.iraFields.stateTaxAmount
      },
      processDate: internalTransferInquiryResponse.processDate,
      iraContributionType: internalTransferInquiryResponse.iraContributionType,
      accountInfoBanner: internalTransferInquiryResponse.accountInfoBanner,
      isThirdParty: internalTransferInquiryResponse.isThirdParty,
      pendReasons: internalTransferInquiryResponse.pendReasons,
      iraContributionTypeMap: internalTransferInquiryResponse.iraContributionTypeMap,
      requestedDate: formatDatePipe.transform(internalTransferInquiryResponse?.auditFields?.request?.date),
      shouldShowStateTax: hasStateTax(
        internalTransferInquiryResponse.iraFields.nraAccountIndicator,
        internalTransferInquiryResponse.iraFields.stwProcessCode as StwProcessCode
      ),
      positionsInfo: internalTransferInquiryResponse.positionsFields,
      stateCd: internalTransferInquiryResponse.stateCd,
      isRMDEligible: internalTransferInquiryResponse.isRMDEligible,
      rmdAnnualRemainingAmount: internalTransferInquiryResponse.rmdAnnualRemainingAmount,
      hasPositionsAccess: internalTransferInquiryResponse.hasPositionsAccess,
      hasPositions: internalTransferInquiryResponse.hasPositions,
      trustFields: internalTransferInquiryResponse.trustFields,
      positionsValidIraContributionCode: internalTransferInquiryResponse.positionsValidIraContributionCode,
      warningsList: internalTransferInquiryResponse.warningList,
      errorText: internalTransferInquiryResponse.errorText,
      taxCodeList: internalTransferInquiryResponse.taxCodeList,
      isTrustNonPTRSAccount: internalTransferInquiryResponse.isTrustNonPTRSAccount,
      pjlsEnabled: internalTransferInquiryResponse.pjlsEnabled,
      hasMoveMoneyDataAccess: internalTransferInquiryResponse.hasMoveMoneyDataAccess,
      cashieringRequestId: internalTransferInquiryResponse.cashieringReqId,
      cashieringRequestSecondId: internalTransferInquiryResponse.cashieringReqSecondaryId,
      cashieringIraAgreementText: internalTransferInquiryResponse.cashieringIraAgreementText
    }
  }),
  on(AccountInfoActions.loadAccountInfoSuccess, (state, info) => {
    return {
      ...state,
      eAuthModel: {
        ...state.eAuthModel,
        custAcctSLOAAuthorizationsList: {
          ...state.eAuthModel.custAcctSLOAAuthorizationsList,
          eAuthCustomerList: UpdateEauthCustomers(state.eAuthModel.custAcctSLOAAuthorizationsList.eAuthCustomerList, info)
        },
        custAcctAuthorizationsList: {
          ...state.eAuthModel.custAcctAuthorizationsList,
          eAuthCustomerList: UpdateEauthCustomers(state.eAuthModel.custAcctAuthorizationsList.eAuthCustomerList, info)
        }
      }
    }
  }),
  on(ViewPageActions.backButtonClicked,
    ViewPageActions.backButtonClickedFromMsv,
    () => {
    return {
      ...initialInternalTransferInfoState
    }
  }),
  on(StandingAuthorizationsActions.loadSloas, (state, _) => {
    return {
      ...state,
      inputCallFinished: false,
      standingAuthCallSucceeded: false,
    };
  }),
  on(
    StandingAuthorizationsActions.loadSloasSuccess,
    (state, _) => {
      return {
        ...state,
        standingAuthCallSucceeded: true,
      };
    }
  ),
  on(
    StandingAuthorizationsActions.loadSloasFailure,
    (state, _) => {
      return {
        ...state,
        standingAuthCallSucceeded: false,
      };
    }
  ),
  on(ReviewPageActions.clearAttentionMessage, (state, _) => {
    return {
      ...state,
      messageInfo: {
        errorList: [],
        warningList: [],
        atsSystemErrors: []
      },
      pendReasons: []
    };
  }),
  on(ReviewPageActions.submitAndCreateToggled, (state, payload) => {
    return {
      ...state,
      isSubmitAndCreate: payload.isSubmitAndCreate,
    }
  }),
  on(ReviewPageActions.editClicked, (state) => {
    return {
      ...state,
      serviceError: '',
      isSubmitAndCreate: false
    }
  }),
  on(ReviewPageActions.saveAsTemplateToggled, (state) => {
    return {
      ...state,
      isSubmitAndCreate: false
    }
  })
);

function hasStateTax(nraIndicator: boolean, stwProcessCode: StwProcessCode): boolean {
  if (nraIndicator) {
    return true;
  } else if (stwProcessCode) {
    return stwProcessCode !== 'N';
  } else {
    return false;
  }
}

function UpdateEauthCustomers(eAuthCustomerList: EAuthModelCustomer[], info: AccountInfoResponse): EAuthModelCustomer[] {
  let list: EAuthModelCustomer[] = [];
  eAuthCustomerList.forEach(item => {
    let update = info.eauthorization.customers.find(customer => {
      return customer.customerId === item.customerId
    })
    if (update) {
      let result: EAuthModelCustomer =
      {
        isAccountWebEnabled: update.isAccountWebEnabled,
        customerWebEnrolled: "false",
        customerId: update.customerId,
        isCustomerWebEnrolled: update.isCustomerWebEnrolled,
        emailId: update.emailId,
        firstName: update.firstName,
        lastName: update.lastName,
        loginIdEnrollStatus: update.loginIdEnrollmentStatus,
        isDisplayAllowed: update.displayIndicator,
        isCustomerEAuthEligible: item.isCustomerEAuthEligible,
        organizationName: update.organizationName,
        eauthInd: item.eauthInd,
        typeCode: update.typeCode,
        customerRestrictionReason: item.customerRestrictionReason,
        accountRoles: update.accountRoles,
        eligibility: item.eligibility
      }
      list.push(result);
    } else {
      list.push(item);
    }
  });
  return list;
}
